import {
  useState,
  createContext,
  useMemo,
  useEffect,
  useCallback,
  useContext
} from 'react';
import AppliedFiltersContext from '~/context/srp/AppliedFiltersContext';
import { useFinanceInfo } from '~/context/srp/FinanceContext';

type ContextType = {
  currentDownPayment?: number;
  currentMonthlyPayment?: number;
  maxMonthlyPayment: number;
  minDownPayment: number;
  minDownPaymentNoTrade?: number;
  setCurrentDownPayment: (_: number | undefined) => void;
  setCurrentMonthlyPayment: (_: number | undefined) => void;
};

const defaultContext: ContextType = {
  currentDownPayment: undefined,
  currentMonthlyPayment: undefined,
  maxMonthlyPayment: 0,
  minDownPayment: 0,
  minDownPaymentNoTrade: undefined,
  setCurrentDownPayment() {},
  setCurrentMonthlyPayment() {}
};

const BudgetContext = createContext<ContextType>(defaultContext);

function useFinanceFilters(): {
  downPayment?: number;
  monthlyPayment?: number;
} {
  const { appliedFilters } = useContext(AppliedFiltersContext);
  const { downPayment, monthlyPayment } = appliedFilters?.finance ?? {};

  return { downPayment, monthlyPayment };
}

export function BudgetContextProvider({ children }) {
  const { monthlyPayment, downPayment } = useFinanceFilters();
  const { maxMonthlyPayment, minDownPayment, minDownPaymentNoTrade } =
    useFinanceInfo();
  const { updateRangeFilter } = useContext(AppliedFiltersContext);
  const [currentDownPayment, setCurrentDownPayment] = useState<
    number | undefined
  >(undefined);
  const [currentMonthlyPayment, setCurrentMonthlyPayment] = useState<
    number | undefined
  >(undefined);

  useEffect(() => {
    setCurrentDownPayment(
      downPayment == null ? downPayment : Math.max(downPayment, minDownPayment)
    );
  }, [downPayment, minDownPayment]);

  useEffect(() => {
    setCurrentMonthlyPayment(
      monthlyPayment == null
        ? monthlyPayment
        : Math.min(monthlyPayment, maxMonthlyPayment)
    );
  }, [maxMonthlyPayment, monthlyPayment]);

  const setCurrentDownPaymentAndUpdateFilter = useCallback(
    (v: number | undefined) => {
      setCurrentDownPayment(v);

      updateRangeFilter({
        filterKey: 'finance',
        value: {
          downPayment: v ?? minDownPayment,
          monthlyPayment: currentMonthlyPayment ?? maxMonthlyPayment
        }
      });
    },
    [
      currentMonthlyPayment,
      maxMonthlyPayment,
      minDownPayment,
      updateRangeFilter
    ]
  );

  const setCurrentMonthlyPaymentAndUpdateFilter = useCallback(
    (v: number | undefined) => {
      setCurrentMonthlyPayment(v);

      updateRangeFilter({
        filterKey: 'finance',
        value: {
          downPayment: currentDownPayment ?? minDownPayment,
          monthlyPayment: v ?? maxMonthlyPayment
        }
      });
    },
    [currentDownPayment, maxMonthlyPayment, minDownPayment, updateRangeFilter]
  );

  const value = useMemo<ContextType>(
    () => ({
      currentDownPayment,
      currentMonthlyPayment,
      maxMonthlyPayment,
      minDownPayment,
      minDownPaymentNoTrade,
      setCurrentDownPayment: setCurrentDownPaymentAndUpdateFilter,
      setCurrentMonthlyPayment: setCurrentMonthlyPaymentAndUpdateFilter
    }),
    [
      currentDownPayment,
      currentMonthlyPayment,
      maxMonthlyPayment,
      minDownPayment,
      minDownPaymentNoTrade,
      setCurrentDownPaymentAndUpdateFilter,
      setCurrentMonthlyPaymentAndUpdateFilter
    ]
  );

  return (
    <BudgetContext.Provider value={value}>{children}</BudgetContext.Provider>
  );
}

export default BudgetContext;
